// Crew side panel: the right-hand crew panel next to any page (Paper "Panel · personal", F).
// A thread rail of tiles (a topic icon, else a letter) sits beside the thread: vertical in the panel (labels on
// hover), a horizontal strip on a phone, and the whole panel when collapsed. Chrome is
// shadcn defaults (ghost icon Buttons with Tooltips, CountBadge, a Popover finder); the
// thread inside uses thread.tsx.
import { Check, ChevronsLeft, ChevronsRight, List, Maximize2, Plus, Search } from "lucide-react";
import { useEffect, useRef, type ComponentProps, type ReactElement, type ReactNode } from "react";
import { Button } from "@crew/ui/components/button";
import { ChatIcon, CHAT_ICON_COMPONENTS } from "@crew/ui/components/chat-icon";
import { AgentAvatar, type AgentTint } from "@crew/ui/components/crew-avatar";
import { IMESSAGE_TINT, ImessageMark } from "@crew/ui/components/imessage-mark";
import { Link } from "@crew/ui/components/link";
import { Popover, PopoverContent, PopoverTrigger } from "@crew/ui/components/popover";
import { CountBadge } from "@crew/ui/components/status";
import { Tooltip, TooltipContent, TooltipTrigger } from "@crew/ui/components/tooltip";
import { useSwipeRightToClose } from "@crew/ui/hooks/use-swipe";
import { cn } from "@crew/ui/lib/utils";

/**
 * Sticky, full-height panel (full screen below `md`). Pass it to the app shell's panel slot.
 * `onSwipeClose`: on a phone, a drag right across it closes it, as Collapse does.
 */
export function SidePanel({
  children,
  className,
  onSwipeClose,
}: {
  children: ReactNode;
  className?: string;
  onSwipeClose?: () => void;
}) {
  const ref = useRef<HTMLElement>(null);
  useSwipeRightToClose(ref, onSwipeClose);
  return (
    <aside
      ref={ref}
      data-slot="side-panel"
      className={cn(
        // Below `md` it covers the screen, over the top and tab bars.
        "fixed inset-0 z-40 flex h-svh w-full flex-col bg-card pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] md:sticky md:pt-0 md:pb-0 md:top-0 md:z-auto md:w-[400px] md:shrink-0 md:flex-row xl:w-[456px] md:border-l md:border-border md:shadow-panel",
        className,
      )}
    >
      {children}
    </aside>
  );
}

/** Ghost icon button with a tooltip label. */
export function PanelIconButton({
  label,
  onClick,
  side = "bottom",
  className,
  children,
}: {
  label: string;
  onClick?: () => void;
  side?: "bottom" | "left" | "right" | "top";
  className?: string;
  children: ReactElement;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            onClick={onClick}
            className={cn("text-muted-foreground hover:text-foreground", className)}
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipContent side={side}>{label}</TooltipContent>
    </Tooltip>
  );
}

/** 52px header over the thread: its title and who is on it, then Open in Chats and Collapse. */
export function SidePanelHeader({
  title,
  mark,
  subtitle,
  subtitleClassName,
  actions,
  onExpand,
  onCollapse,
}: {
  title: string;
  /** Before the title: e.g. a messaging thread's app mark. */
  mark?: ReactNode;
  subtitle?: string;
  /** A text-colour class, e.g. `text-moss` while an agent works. */
  subtitleClassName?: string;
  /** Before the icon buttons: e.g. the thread's task chip. */
  actions?: ReactNode;
  onExpand?: () => void;
  onCollapse?: () => void;
}) {
  return (
    <div
      data-slot="side-panel-header"
      className="flex h-[52px] shrink-0 items-center gap-1 border-b border-border pr-2 pl-5"
    >
      <div className="flex min-w-0 flex-1 flex-col">
        {mark ? (
          <div className="flex min-w-0 items-center gap-1.5">
            {mark}
            <h2 className="truncate text-nav font-semibold text-foreground">{title}</h2>
          </div>
        ) : (
          <h2 className="truncate text-nav font-semibold text-foreground">{title}</h2>
        )}
        {subtitle ? (
          <span className={cn("truncate text-xs text-muted-foreground", subtitleClassName)}>
            {subtitle}
          </span>
        ) : null}
      </div>
      {actions}
      {onExpand ? (
        <PanelIconButton label="Open in Chats" onClick={onExpand}>
          <Maximize2 />
        </PanelIconButton>
      ) : null}
      {onCollapse ? (
        <PanelIconButton label="Collapse panel" onClick={onCollapse}>
          <ChevronsRight />
        </PanelIconButton>
      ) : null}
    </div>
  );
}

export type TileTint = "peach" | "sand" | "sky" | "lilac" | "sage" | "clay";

const tileTint: Record<TileTint, string> = {
  peach: "bg-tile-peach",
  sand: "bg-tile-sand",
  sky: "bg-tile-sky",
  lilac: "bg-tile-lilac",
  sage: "bg-tile-sage",
  clay: "bg-tile-clay",
};

/** Today (slice 4b §5) rings a chat that waits on the person in copper. */
export type TileStatus = "working" | "queued" | "waiting" | null;

const tileRing: Record<NonNullable<TileStatus>, string> = {
  working: "shadow-[0_0_0_2px_var(--color-rail),0_0_0_3.5px_var(--color-moss)]",
  queued: "shadow-[0_0_0_2px_var(--color-rail),0_0_0_3.5px_var(--color-hint)]",
  waiting: "shadow-[0_0_0_2px_var(--color-rail),0_0_0_3.5px_var(--color-copper)]",
};

/** Who a thread is with, for the tile's corner and the labels. */
export type TileAgent = { name: string; glyph: string; tint: AgentTint };

/**
 * A thread's tile: its topic icon (Phosphor Duotone, spike S-324), else a letter, on
 * its tint; a texted chat's is on iMessage's pale blue, its topic icon once it has one
 * (slice 2i: a person has several), else iMessage's bubble. Ringed moss while an
 * agent works, grey while queued, and the agent it's with in the bottom corner.
 */
export function ThreadTile({
  letter,
  tint,
  status,
  agent,
  icon,
  imessage = false,
  size = 34,
  className,
}: {
  letter: string;
  tint: TileTint;
  status: TileStatus;
  agent?: TileAgent | null;
  /** A name from the chat icon list; an unknown one shows the letter. */
  icon?: string | null;
  imessage?: boolean;
  size?: number;
  className?: string;
}) {
  const glyph = Math.round(size * (19 / 34));
  return (
    <span
      data-slot="thread-tile"
      aria-hidden
      className={cn("relative flex shrink-0", className)}
      style={{ width: size, height: size }}
    >
      <span
        className={cn(
          "flex size-full items-center justify-center rounded-[9px] text-[13px] font-semibold text-foreground",
          !imessage && tileTint[tint],
          status && tileRing[status],
        )}
        style={imessage ? { background: IMESSAGE_TINT } : undefined}
      >
        {icon && CHAT_ICON_COMPONENTS[icon] ? (
          <ChatIcon name={icon} size={glyph} />
        ) : imessage ? (
          <ImessageMark size={Math.round(size * (20 / 34))} />
        ) : (
          letter
        )}
      </span>
      {agent ? (
        <AgentAvatar
          glyph={agent.glyph}
          tint={agent.tint}
          size={17}
          className="absolute -right-[5px] -bottom-[5px] shadow-[0_0_0_2px_var(--color-rail)]"
        />
      ) : null}
    </span>
  );
}

export type RailThread = {
  id: string;
  title: string;
  href: string;
  letter: string;
  tint: TileTint;
  status: TileStatus;
  /** Its topic icon (S-324), shown instead of the letter once it's picked. */
  icon?: string | null;
  /** A texted chat: iMessage's pale blue, with its topic icon once it has one. */
  imessage?: boolean;
  /** Beside the title when the rail opens: "Working", "Queued", or when it last moved. */
  meta: string;
  metaClassName?: string;
  /** Who the thread is with: its corner, and named beside the title. */
  agent?: TileAgent | null;
};

/** What a screen reader hears for a tile: the title, who it's with, and what it's doing. */
const tileLabel = (thread: RailThread) =>
  [thread.title, thread.agent?.name, thread.status && thread.meta.toLowerCase()]
    .filter(Boolean)
    .join(", ");

/** Beside or under a title: who the thread is with, then its status or time. */
const railMeta = (thread: RailThread) =>
  thread.agent ? `${thread.agent.name} · ${thread.meta}` : thread.meta;

function NewTile({ size = 34 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="flex shrink-0 items-center justify-center rounded-[9px] bg-primary text-primary-foreground"
      style={{ width: size, height: size }}
    >
      <Plus className="size-4" />
    </span>
  );
}

/** The rail's "+N" button: every thread, in a finder. Pass it as `ThreadFinder`'s trigger. */
export function RailMoreButton({
  count,
  className,
  ...props
}: ComponentProps<"button"> & { count: number }) {
  return (
    <button
      type="button"
      aria-label="All threads"
      className={cn(
        "flex size-10 shrink-0 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-[9px] text-muted-foreground outline-none hover:bg-accent hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring data-popup-open:bg-muted",
        className,
      )}
      {...props}
    >
      <List className="size-4" />
      {count > 0 ? <span className="text-[10px] leading-3">+{count}</span> : null}
    </button>
  );
}

/**
 * The panel's vertical rail (56px): New, then each thread's tile, and "+N" at the
 * bottom. Resting on the tiles (or tabbing into them) widens the rail in place to
 * 280px with each title beside its tile.
 * Hidden below `md`, where `ThreadStrip` takes its place.
 */
export function ThreadRail({
  threads,
  current,
  newHref,
  more,
  className,
}: {
  threads: RailThread[];
  /** The open thread's id, or "new" on the new-thread screen. */
  current?: string;
  newHref: string;
  more?: ReactNode;
  className?: string;
}) {
  return (
    <nav
      aria-label="Threads"
      data-slot="thread-rail"
      className={cn("relative hidden w-14 shrink-0 md:block", className)}
    >
      <div className="absolute inset-y-0 left-0 z-30 flex w-14 flex-col gap-3 overflow-hidden bg-rail pt-3.5 pb-16 pl-[11px] shadow-[inset_-1px_0_0_var(--color-border)] transition-[width,box-shadow] duration-150 focus-within:w-[280px] focus-within:shadow-[1px_0_0_var(--color-border),12px_0_32px_rgb(31_42_68/12%)] hover:w-[280px] hover:shadow-[1px_0_0_var(--color-border),12px_0_32px_rgb(31_42_68/12%)] hover:delay-300">
        <RailRow href={newHref} current={current === "new"} label="New thread">
          <NewTile />
          <span className="text-nav font-medium text-foreground">New thread</span>
        </RailRow>
        <span className="ml-[5px] h-px w-6 shrink-0 bg-border" />
        {/* The tiles scroll on a short screen; the margin keeps the open bar and rings in view. */}
        <div className="-ml-[11px] flex min-h-0 flex-col gap-3 overflow-x-hidden overflow-y-auto py-1 pl-[11px] [scrollbar-width:none]">
          {threads.map((thread) => (
            <RailRow
              key={thread.id}
              href={thread.href}
              current={thread.id === current}
              label={tileLabel(thread)}
              threadId={thread.id}
            >
              <ThreadTile
                letter={thread.letter}
                tint={thread.tint}
                status={thread.status}
                agent={thread.agent}
                icon={thread.icon}
                imessage={thread.imessage}
              />
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-meta font-medium text-foreground">
                  {thread.title}
                </span>
                <span
                  className={cn("truncate text-xs text-muted-foreground", thread.metaClassName)}
                >
                  {railMeta(thread)}
                </span>
              </span>
            </RailRow>
          ))}
        </div>
      </div>
      {/* Outside the part that widens, so opening the finder leaves the rail narrow. */}
      {more ? <div className="absolute bottom-3 left-2 z-40">{more}</div> : null}
    </nav>
  );
}

function RailRow({
  href,
  current,
  label,
  threadId,
  children,
}: {
  href: string;
  current: boolean;
  label: string;
  threadId?: string;
  children: ReactNode;
}) {
  return (
    <Link
      href={href}
      aria-label={label}
      aria-current={current ? "page" : undefined}
      data-thread-id={threadId}
      className="relative flex h-[34px] w-[257px] shrink-0 items-center gap-3 rounded-[9px] whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {current ? (
        <span className="absolute top-[7px] -left-[11px] h-5 w-[3px] rounded-r-sm bg-foreground" />
      ) : null}
      {children}
    </Link>
  );
}

/**
 * Scroll the strip sideways, and only the strip, so `tile` shows clear of its left edge
 * and of the pinned "+N" on the right.
 */
function revealInStrip(strip: HTMLElement, tile: HTMLElement) {
  const start = tile.offsetLeft - 16;
  const end = tile.offsetLeft + tile.offsetWidth + 64 - strip.clientWidth;
  if (start < strip.scrollLeft) strip.scrollLeft = start;
  else if (end > strip.scrollLeft) strip.scrollLeft = end;
}

/** The phone's rail: New and the tiles in a row under the header, "+N" pinned at the end. */
export function ThreadStrip({
  threads,
  current,
  newHref,
  more,
  className,
}: {
  threads: RailThread[];
  current?: string;
  newHref: string;
  more?: ReactNode;
  className?: string;
}) {
  // Keep the open thread's tile in view, e.g. after picking it from the finder.
  const scroller = useRef<HTMLDivElement>(null);
  const shown = threads.some((t) => t.id === current);
  useEffect(() => {
    const strip = scroller.current;
    const tile = strip?.querySelector<HTMLElement>('[aria-current="page"]');
    if (strip && tile) revealInStrip(strip, tile);
  }, [current, shown]);
  return (
    <nav
      aria-label="Threads"
      data-slot="thread-strip"
      className={cn(
        "relative flex h-[60px] shrink-0 items-center border-b border-border bg-rail md:hidden",
        className,
      )}
    >
      <div
        ref={scroller}
        className="relative flex h-full min-w-0 flex-1 items-center gap-3 overflow-x-auto pr-16 pl-4 [scrollbar-width:none]"
      >
        <Link
          href={newHref}
          aria-label="New thread"
          aria-current={current === "new" ? "page" : undefined}
        >
          <NewTile size={36} />
        </Link>
        <span className="h-6 w-px shrink-0 bg-border" />
        {threads.map((thread) => (
          <Link
            key={thread.id}
            href={thread.href}
            aria-label={tileLabel(thread)}
            aria-current={thread.id === current ? "page" : undefined}
            data-thread-id={thread.id}
            className="relative shrink-0"
          >
            <ThreadTile
              letter={thread.letter}
              tint={thread.tint}
              status={thread.status}
              agent={thread.agent}
              icon={thread.icon}
              imessage={thread.imessage}
              size={36}
            />
            {thread.id === current ? (
              <span className="absolute -bottom-3 left-2 h-[3px] w-5 rounded-t-sm bg-foreground" />
            ) : null}
          </Link>
        ))}
      </div>
      {more ? (
        <div className="absolute inset-y-0 right-0 flex items-center bg-[linear-gradient(90deg,transparent,var(--color-rail)_34%)] pr-3 pl-7">
          {more}
        </div>
      ) : null}
    </nav>
  );
}

/**
 * The panel when collapsed (56px, desktop only): expand, New, each thread's tile with its
 * title in a tooltip, "+N", and the waiting count at the bottom.
 */
export function CollapsedRail({
  threads,
  newHref,
  waiting,
  onExpand,
  more,
  className,
}: {
  threads: RailThread[];
  newHref: string;
  waiting?: number;
  onExpand?: () => void;
  more?: ReactNode;
  className?: string;
}) {
  return (
    <aside
      data-slot="collapsed-rail"
      className={cn(
        // Not on a phone: the top bar opens the panel instead.
        "sticky top-0 hidden h-svh w-14 shrink-0 flex-col items-center gap-3 border-l border-border bg-rail py-3.5 md:flex",
        className,
      )}
    >
      <PanelIconButton label="Open crew panel" side="left" onClick={onExpand}>
        <ChevronsLeft />
      </PanelIconButton>
      <Tooltip>
        <TooltipTrigger
          render={<Link href={newHref} aria-label="New thread" className="rounded-[9px]" />}
        >
          <NewTile />
        </TooltipTrigger>
        <TooltipContent side="left">New thread</TooltipContent>
      </Tooltip>
      <span className="h-px w-6 shrink-0 bg-border" />
      <div className="flex min-h-0 flex-col items-center gap-3 overflow-y-auto p-1 [scrollbar-width:none]">
        {threads.map((thread) => (
          <Tooltip key={thread.id}>
            <TooltipTrigger
              render={
                <Link
                  href={thread.href}
                  aria-label={tileLabel(thread)}
                  data-thread-id={thread.id}
                  className="rounded-[9px]"
                />
              }
            >
              <ThreadTile
                letter={thread.letter}
                tint={thread.tint}
                status={thread.status}
                agent={thread.agent}
                icon={thread.icon}
                imessage={thread.imessage}
              />
            </TooltipTrigger>
            <TooltipContent side="left">
              {thread.title} · {railMeta(thread)}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
      <span className="min-h-0 flex-1" />
      {more}
      {waiting ? (
        <div className="flex flex-col items-center gap-1">
          <CountBadge>{waiting}</CountBadge>
          <span className="text-[10px] leading-3 text-muted-foreground">waiting</span>
        </div>
      ) : null}
    </aside>
  );
}

/**
 * Every thread in a popover: a search box, the rows (`ThreadFinderItem`) and a footer.
 * `trigger` is the element that opens it, such as `RailMoreButton`.
 */
export function ThreadFinder({
  trigger,
  open,
  onOpenChange,
  query,
  onQueryChange,
  footer,
  side = "right",
  children,
}: {
  trigger: ReactElement;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  query: string;
  onQueryChange: (query: string) => void;
  footer?: ReactNode;
  side?: "right" | "left" | "bottom";
  children: ReactNode;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger render={trigger} />
      <PopoverContent
        side={side}
        align="end"
        sideOffset={8}
        className="w-[min(320px,calc(100vw-1rem))] gap-0.5 rounded-panel p-1.5"
      >
        <label className="flex h-9 items-center gap-2 border-b border-border px-2.5">
          <Search className="size-3.5 shrink-0 text-hint" />
          <input
            type="search"
            aria-label="Find a thread"
            placeholder="Find a thread"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-nav outline-none placeholder:text-hint"
          />
        </label>
        <div role="list" className="flex max-h-96 flex-col gap-0.5 overflow-y-auto pt-1">
          {children}
        </div>
        {footer ? (
          <div className="flex items-center border-t border-border px-2.5 pt-2 pb-1 text-meta text-muted-foreground">
            {footer}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/** A finder row's first column: a check on the current thread, else its dot, else nothing. */
function StatusSlot({ current, dot }: { current: boolean; dot?: string }) {
  let mark: ReactNode = null;
  if (current) mark = <Check className="size-3.5" strokeWidth={2.25} />;
  else if (dot) mark = <span className={cn("size-[7px] rounded-full bg-current", dot)} />;
  return <span className="flex size-3.5 shrink-0 items-center justify-center">{mark}</span>;
}

/** A finder row's second line: the agent's face and name. */
function FinderAgent({ agent }: { agent?: TileAgent | null }) {
  if (!agent) return null;
  return (
    <span className="flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
      <AgentAvatar glyph={agent.glyph} tint={agent.tint} size={14} />
      <span className="truncate">{agent.name}</span>
    </span>
  );
}

/** One finder row: a status slot, the title, and a trailing meta. */
export function ThreadFinderItem({
  href,
  current,
  dot,
  meta,
  metaClassName,
  agent,
  onClick,
  children,
}: {
  href: string;
  current: boolean;
  /** Who the thread is with, named under the title. */
  agent?: TileAgent | null;
  /** A text-colour class for the dot; none leaves the slot empty. */
  dot?: string;
  meta?: ReactNode;
  metaClassName?: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <Link
      href={href}
      role="listitem"
      aria-current={current ? "true" : undefined}
      onClick={onClick}
      className={cn(
        "flex items-center gap-2.5 rounded-md px-2.5 py-3 text-nav text-foreground outline-none hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring md:py-2",
        current && "bg-accent font-medium",
      )}
    >
      <StatusSlot current={current} dot={dot} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate">{children}</span>
        <FinderAgent agent={agent} />
      </span>
      <span className={cn("shrink-0 text-xs text-muted-foreground", metaClassName)}>{meta}</span>
    </Link>
  );
}
